import React from "react";
import {
  Users,
  Eye,
  SealCheck,
  CheckCircle,
  Briefcase,
  ShieldCheck,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import {
  useTotalUsers,
  useViewUsers,
  useVerifyUsers,
  useApproveUsers,
  useHeadUsers,
  useAdminUsers,
} from "../../adapters/Requests";
import { Card, CardContent } from "../ui/card";

export default function UsersSummary() {
  const { data: totalData, isLoading: totalLoading } = useTotalUsers();
  const { data: viewData, isLoading: viewLoading } = useViewUsers();
  const { data: verifyData, isLoading: verifyLoading } = useVerifyUsers();
  const { data: approveData, isLoading: approveLoading } = useApproveUsers();
  const { data: headData, isLoading: headLoading } = useHeadUsers();
  const { data: adminData, isLoading: adminLoading } = useAdminUsers();

  const isLoading =
    totalLoading ||
    viewLoading ||
    verifyLoading ||
    approveLoading ||
    headLoading ||
    adminLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-32">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    );
  }

  const stats = [
    {
      label: "Total Users",
      value: totalData?.users || 0,
      icon: Users,
      color: "text-blue-600",
      bg: "bg-blue-100",
    },
    {
      label: "Viewers",
      value: viewData?.viewUsers || 0,
      icon: Eye,
      color: "text-gray-600",
      bg: "bg-gray-100",
    },
    {
      label: "Verifiers",
      value: verifyData?.verifyUsers || 0,
      icon: SealCheck,
      color: "text-indigo-600",
      bg: "bg-indigo-100",
    },
    {
      label: "Approvers",
      value: approveData?.approveUsers || 0,
      icon: CheckCircle,
      color: "text-teal-600",
      bg: "bg-teal-100",
    },
    {
      label: "Heads (HOD)",
      value: headData?.headUsers || 0,
      icon: Briefcase,
      color: "text-orange-600",
      bg: "bg-orange-100",
    },
    {
      label: "Admins",
      value: adminData?.adminUsers || 0,
      icon: ShieldCheck,
      color: "text-red-600",
      bg: "bg-red-100",
    },
  ];

  return (
    <div className="w-full mt-6 max-w-[95%] xl:max-w-[80%] mx-auto mb-3">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider ml-1">
          Users Overview
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="flex flex-col items-center justify-center p-3 rounded-lg border border-slate-100 bg-slate-50 cursor-default select-none"
          >
            <div
              className={`h-8 w-8 rounded-full flex items-center justify-center mb-2 ${stat.bg} ${stat.color}`}
            >
              <stat.icon size={16} weight="fill" />
            </div>
            <h3 className="text-lg font-bold text-slate-700 leading-none">
              {stat.value}
            </h3>
            <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mt-1">
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
